import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { toast } from 'sonner';
import { notify } from '@/utils/notifications';
import { setFile, getFiles, Time, FileItem } from '@/lib/db-client';
import { setUserAvatars } from '@/lib/collections/userAvatars';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AvatarCropModalProps {
  imageSrc: string;
  walletAddress: string;
  onClose: () => void;
  onSuccess: (avatarUrl: string) => void;
}

// ─── Canvas crop helper ───────────────────────────────────────────────────────

async function getCroppedBlob(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const OUTPUT_SIZE = 512;
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas context unavailable')); return; }

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE,
      );

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas toBlob failed'));
        },
        'image/jpeg',
        0.85,
      );
    };
    image.onerror = () => reject(new Error('Image load failed'));
    image.src = imageSrc;
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

const AvatarCropModal: React.FC<AvatarCropModalProps> = ({
  imageSrc,
  walletAddress,
  onClose,
  onSuccess,
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_croppedArea: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      // 1. Generate cropped blob at 512×512 JPEG
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);

      // 2. Upload to Poof file storage
      const filename = `avatar-${walletAddress}-${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: 'image/jpeg' });
      const uploadPath = `appFiles/${filename}`;

      const uploaded = await setFile(uploadPath, file);
      if (!uploaded) {
        notify.error('Upload failed. Please try again.', walletAddress);
        return;
      }

      // Fetch the file record to get the public URL
      const filesResult = await getFiles(uploadPath) as FileItem[] | null;
      const fileRecord = Array.isArray(filesResult) ? filesResult[0] : null;
      const avatarUrl = fileRecord?.url ?? '';

      if (!avatarUrl) {
        notify.error('Upload succeeded but URL was missing. Please try again.', walletAddress);
        return;
      }

      // 3. Write the URL to the userAvatars collection
      const success = await setUserAvatars(walletAddress, {
        avatarUrl,
        updatedAt: Time.Now,
      });

      if (success) {
        onSuccess(avatarUrl);
        onClose();
      } else {
        notify.error('Failed to save avatar. Check wallet permissions.', walletAddress);
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      notify.error(err instanceof Error ? err.message : 'Something went wrong uploading your avatar.', walletAddress);
    } finally {
      setSaving(false);
    }
  };

  return (
    /* Backdrop */
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          borderRadius: 20,
          background: 'rgba(18,18,24,0.98)',
          border: '1px solid rgba(167,139,250,0.18)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(167,139,250,0.08)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '18px 20px 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{
            fontSize: 17, fontWeight: 700, color: '#f0f0f5',
            fontFamily: "'Syne', sans-serif", letterSpacing: '-0.01em',
          }}>
            Crop Avatar
          </span>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'rgba(255,255,255,0.07)', border: 'none',
              borderRadius: 8, width: 32, height: 32, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(240,240,245,0.5)', fontSize: 18, transition: 'background 0.15s',
            }}
          >
            ✕
          </button>
        </div>

        {/* Crop area */}
        <div style={{
          position: 'relative',
          width: '100%', height: 320,
          background: '#0a0a0f',
          margin: '16px 0 0',
        }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{
              containerStyle: { background: '#0a0a0f' },
              cropAreaStyle: {
                border: '2px solid rgba(167,139,250,0.8)',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
              },
            }}
          />
        </div>

        {/* Zoom slider */}
        <div style={{ padding: '16px 20px 4px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'rgba(240,240,245,0.4)', fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>
            Zoom
          </span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{
              flex: 1,
              accentColor: '#a78bfa',
              height: 4,
              cursor: 'pointer',
              background: 'transparent',
            }}
          />
        </div>

        {/* Actions */}
        <div style={{
          padding: '14px 20px 20px',
          display: 'flex', gap: 10,
        }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1, height: 44, borderRadius: 12,
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(240,240,245,0.7)',
              fontSize: 15, fontWeight: 600,
              fontFamily: "'Syne', sans-serif",
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1.5, height: 44, borderRadius: 12,
              background: saving
                ? 'rgba(167,139,250,0.3)'
                : 'linear-gradient(135deg, #a78bfa 0%, #818cf8 100%)',
              border: 'none',
              color: saving ? 'rgba(240,240,245,0.5)' : '#fff',
              fontSize: 15, fontWeight: 700,
              fontFamily: "'Syne', sans-serif",
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {saving ? (
              <>
                <span style={{
                  width: 16, height: 16, borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff',
                  animation: 'spin 0.7s linear infinite',
                  display: 'inline-block',
                  flexShrink: 0,
                }} />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default AvatarCropModal;
